import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Guards
import {
  HasContractGuard,
  legacyUrlRedirectGuard,
  requireOnboardingGuard,
  requirePendingOnboardingGuard,
  mobileDeviceGuard,
} from '@tgx/shared/guards';

// UI Components
import { ErrorPageComponent, NoContractPageComponent, MobileComponent } from '@tgx/shared/ui/components';
import { StatusViewComponent } from './components/public/status-view/status-view.component';
import { V3SignUpComponent } from './components/public/v3-sign-up/v3-sign-up.component';
import { XmlRedirectComponent } from './components/public/xml_redirect/xml-redirect.component';

// =================================================================
// STEP 1: Define route lists by logic type
// =================================================================

// Redirects from old or short routes to their new locations
const redirectRoutes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'alerts', redirectTo: 'monitoring/alerts', pathMatch: 'full' },
  { path: 'b2b', redirectTo: 'solutions/b2b', pathMatch: 'full' },
  { path: 'errors-overview', redirectTo: 'insights/errors-overview', pathMatch: 'full' },
  { path: 'hotel-portfolio', redirectTo: 'insights/hotel-portfolio', pathMatch: 'full' },
  { path: 'logging', redirectTo: 'monitoring/logging', pathMatch: 'full' },
  { path: 'insights', redirectTo: 'insights/errors-overview', pathMatch: 'full' },
  { path: 'monitoring', redirectTo: 'monitoring/stats', pathMatch: 'full' },
  { path: 'network', redirectTo: 'network/partners', pathMatch: 'full' },
  { path: 'partners', redirectTo: 'network/partners', pathMatch: 'full' },
  { path: 'roadmap', redirectTo: 'network/roadmap', pathMatch: 'full' },
  { path: 'smart-traffic', redirectTo: 'smart-traffic/speed', pathMatch: 'full' },
  { path: 'solutions', redirectTo: 'solutions/b2b', pathMatch: 'full' },
  { path: 'speed', redirectTo: 'smart-traffic/speed', pathMatch: 'full' },
  { path: 'stats', redirectTo: 'monitoring/stats', pathMatch: 'full' },
  { path: 'tickets', redirectTo: 'support', pathMatch: 'full' },
  { path: 'tickets/:id', redirectTo: 'support/:id', pathMatch: 'full' },
  { path: 'traffic-optimizer', redirectTo: 'smart-traffic/traffic-optimizer', pathMatch: 'full' },
];

// Routes that require full protection (Contract + completed Onboarding)
const fullyProtectedFeatureRoutes: Routes = [
  {
    path: 'connections',
    loadChildren: () => import('@tgx/web/feature-connections').then((m) => m.ConnectionsModule),
  },
  {
    path: 'dashboard',
    loadChildren: () => import('@tgx/web/feature-dashboard').then((m) => m.DashboardModule),
  },
  {
    path: 'insights/errors-overview',
    loadChildren: () => import('@tgx/web/feature-errors-overview').then((m) => m.ErrorsOverviewModule),
  },
  {
    path: 'insights/hotel-portfolio',
    loadChildren: () => import('@tgx/web/feature-hotel-portfolio').then((m) => m.HotelPortfolioModule),
  },
  {
    path: 'monitoring/alerts',
    loadChildren: () => import('@tgx/web/feature-alerts').then((m) => m.AlertsModule),
  },
  {
    path: 'monitoring/logging',
    loadChildren: () => import('@tgx/web/feature-logging').then((m) => m.LoggingModule),
  },
  {
    path: 'monitoring/stats',
    loadChildren: () => import('@tgx/web/feature-stats').then((m) => m.StatsModule),
  },
  {
    path: 'network/partners',
    loadChildren: () => import('@tgx/web/feature-network-v2').then((m) => m.NetworkV2Module),
  },
  {
    path: 'settings',
    loadChildren: () => import('@tgx/web/feature-settings').then((m) => m.SettingsModule),
  },
  {
    path: 'smart-traffic/speed',
    loadChildren: () => import('@tgx/web/feature-speed').then((m) => m.SpeedModule),
  },
  {
    path: 'smart-traffic/traffic-optimizer',
    loadChildren: () => import('@tgx/web/feature-traffic-op').then((m) => m.TrafficOpModule),
  },
  {
    path: 'solutions/b2b',
    loadChildren: () => import('@tgx/web/feature-b2b').then((m) => m.B2bModule),
  },
  {
    path: 'support',
    loadChildren: () => import('@tgx/web/feature-tickets').then((m) => m.TicketsModule),
  },
  {
    path: 'travelgate-store',
    loadChildren: () => import('@tgx/web/feature-app-store').then((m) => m.AppStoreModule),
  },
];

// =================================================================
// STEP 2: Build main routing with containers
// =================================================================

const routes: Routes = [
  // 1. General Redirects
  ...redirectRoutes,

  // 2. Public routes or routes with specific protection logic
  {
    path: 'onboarding-company',
    loadChildren: () => import('@tgx/web/feature-onboarding-company').then((m) => m.OnboardingCompanyModule),
    canActivate: [mobileDeviceGuard],
  },
  {
    path: 'onboarding-implementation',
    loadChildren: () =>
      import('@tgx/web/feature-onboarding-implementation').then((m) => m.OnboardingImplementationModule),
    canActivate: [mobileDeviceGuard, requirePendingOnboardingGuard],
    canActivateChild: [requirePendingOnboardingGuard],
  },
  { path: 'company-registration', component: V3SignUpComponent },
  { path: 'status', component: StatusViewComponent },
  { path: 'xml-redirect', component: XmlRedirectComponent },
  { path: 'signup', redirectTo: 'company-registration', pathMatch: 'full' },
  {
    path: 'network/roadmap',
    loadChildren: () => import('@tgx/web/feature-roadmap').then((m) => m.RoadmapModule),
  },
  {
    path: 'network/partners',
    loadChildren: () => import('@tgx/web/feature-network-v2').then((m) => m.NetworkV2Module),
  },
  { path: 'no-contract', component: NoContractPageComponent },
  { path: 'mobile', component: MobileComponent, pathMatch: 'full', canActivate: [mobileDeviceGuard] },
  { path: '401', component: ErrorPageComponent, data: { virtualStatus: 401 } },
  { path: '404', component: ErrorPageComponent, data: { virtualStatus: 404 } },
  { path: '500', component: ErrorPageComponent, data: { virtualStatus: 500 } },
  { path: '503', component: ErrorPageComponent, data: { virtualStatus: 503 } },

  // 3. Main container for protected routes
  {
    path: '',
    canActivate: [mobileDeviceGuard, HasContractGuard],
    canActivateChild: [requireOnboardingGuard],
    children: [...fullyProtectedFeatureRoutes],
  },

  // 4. Legacy org/type route used only to redirect to canonical URLs
  {
    path: ':org/:type',
    children: [
      {
        path: '**',
        canActivate: [legacyUrlRedirectGuard],
        component: ErrorPageComponent,
        data: { virtualStatus: 404 },
      },
    ],
  },

  // 5. Catch-all (wildcard) always at the end
  { path: '**', component: ErrorPageComponent, data: { virtualStatus: 404 } },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
